import { Platform } from 'react-native';
import { currentEnvironment } from '../config/environments';

// Lazy-load store to avoid circular dependency
// This getter function is called only when needed, breaking the circular import chain
let _store: any = null;
const getStore = () => {
  if (!_store) {
    _store = require('../store').store;
  }
  return _store;
};

// Platform-specific storage
let storage: {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
};

if (Platform.OS === 'web') {
  // Web storage using localStorage
  storage = {
    getItem: async (key: string) => {
      return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
    },
    setItem: async (key: string, value: string) => {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, value);
      }
    },
    removeItem: async (key: string) => {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(key);
      }
    },
  };
} else {
  // Mobile storage using AsyncStorage
  const AsyncStorage = require('@react-native-async-storage/async-storage').default;
  storage = AsyncStorage;
}

const API_BASE_URL = currentEnvironment.apiUrl;

export type PdfExportJobStatus = 'queued' | 'processing' | 'completed' | 'failed';

export interface PdfExportJobStartResponse {
  jobId: string;
  status: PdfExportJobStatus;
}

export interface PdfExportJobStatusResponse extends PdfExportJobStartResponse {
  errorMessage?: string | null;
  downloadUrl?: string;
  expiresAt?: string;
}

class ApiService {
  private baseURL: string;
  public token: string | null = null;

  constructor() {
    this.baseURL = API_BASE_URL;
    // Initialize token from storage on web
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      this.initializeToken();
    }
  }

  private async initializeToken() {
    try {
      const storedToken = await storage.getItem('token');
      if (storedToken) {
        this.token = storedToken;
      }
    } catch (error) {
    }
  }

  private async getHeaders(skipContentType = false): Promise<HeadersInit> {
    const token = await this.getToken();
    const headers: Record<string, string> = {
      ...(token && { Authorization: `Bearer ${token}` }),
    };
    // Only set Content-Type for JSON requests, not for FormData (which sets its own boundary)
    if (!skipContentType) {
      headers['Content-Type'] = 'application/json';
    }
    return headers;
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    // Check if body is FormData to skip Content-Type header
    const isFormData = options.body instanceof FormData;
    const headers = await this.getHeaders(isFormData);

    try {
      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
        signal: controller.signal,
      }).finally(() => clearTimeout(timeoutId));

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        let error: any = {};
        try {
          error = errorText ? JSON.parse(errorText) : {};
        } catch (e) {
          error = { message: errorText };
        }

        // Create error with response attached
        const err: any = new Error(error.message || `Request failed: ${response.status}`);
        err.response = { data: error, status: response.status };
        
        // Provide user-friendly error messages
        if (response.status === 401 && endpoint.includes('/auth/login')) {
          throw new Error('Invalid username or password');
        } else if (response.status === 401) {
          // Clear auth state and redirect to login
          await this.clearToken();
          getStore().dispatch({ type: 'auth/authFailed' });
          throw new Error('Session expired. Please login again.');
        } else if (response.status === 403) {
          throw new Error('Access denied');
        } else if (response.status === 404) {
          throw new Error('Resource not found');
        } else if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }
        throw err;
      }

      // Handle empty response body (like 204 No Content)
      const text = await response.text();
      if (!text) {
        return {} as T;
      }
      
      const data = JSON.parse(text);

      // Handle standardized response format from backend.
      if (data && typeof data === 'object' && 'success' in data) {
        if (data.success === false) {
          throw new Error(data.message || 'Request failed');
        }
        if ('data' in data) {
          return data.data;
        }
        return data;
      }

      throw new Error('Invalid API response format');
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please check your connection and try again.');
      }
      throw error;
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string, options?: { data?: any }): Promise<T> {
    return this.request<T>(endpoint, { 
      method: 'DELETE',
      body: options?.data ? JSON.stringify(options.data) : undefined
    });
  }

  async uploadFiles<T>(
    endpoint: string,
    files: Array<{ file?: File; uri?: string; name: string; type: string }>
  ): Promise<T> {
    const formData = new FormData();

    files.forEach((fileObj) => {
      if (Platform.OS === 'web' && fileObj.file) {
        // Web file upload - use the actual File object
        formData.append('photos', fileObj.file, fileObj.name);
      } else if (fileObj.uri) {
        // Mobile file upload - use uri format
        formData.append('photos', {
          uri: fileObj.uri,
          name: fileObj.name,
          type: fileObj.type,
        } as any);
      }
    });

    // Use ApiService.request to ensure consistent fetch behavior with JSON API calls
    return this.request<T>(endpoint, {
      method: 'POST',
      body: formData,
    });
  }

  async setToken(token: string | null): Promise<void> {
    this.token = token;
    if (token) {
      await storage.setItem('token', token);
      
      // Verify it was stored
      const verifyToken = await storage.getItem('token');
    } else {
      await storage.removeItem('token');
    }
  }

  async getToken(): Promise<string | null> {
    if (!this.token) {
      const storedToken = await storage.getItem('token');
      this.token = storedToken;
    }
    return this.token;
  }

  async clearToken(): Promise<void> {
    this.token = null;
    await storage.removeItem('token');
  }

  async startReviewPDFExportJob(reviewId: number): Promise<PdfExportJobStartResponse> {
    return this.post<PdfExportJobStartResponse>(`/reviews/${reviewId}/export/pdf/jobs`);
  }

  async getReviewPDFExportJob(reviewId: number, jobId: string): Promise<PdfExportJobStatusResponse> {
    return this.get<PdfExportJobStatusResponse>(`/reviews/${reviewId}/export/pdf/jobs/${jobId}`);
  }

  async exportReviewPDF(reviewId: number): Promise<Blob> {
    const token = await storage.getItem('token');
    const response = await fetch(`${this.baseURL}/reviews/${reviewId}/export/pdf`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to export PDF' }));
      throw new Error(error.message || 'Failed to export PDF');
    }

    return response.blob();
  }
}

export const api = new ApiService();
